import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService, LoginResult, UserRole } from '@app/core/auth/auth.service';
import { MessageBoxIcon, ModalButtons } from '@app/core/models/modal-ref';
import { ModalService } from '@app/core/services/modal.service';
import { NGXLogger } from 'ngx-logger';
import { combineLatest } from 'rxjs';


/**
 * Performs login using a login token that was passed via URL
 */
@Component({
  selector: 'app-token-login',
  templateUrl: './token-login.component.html',
  styleUrls: ['./token-login.component.scss']
})
export class TokenLoginComponent implements OnInit {

  constructor(
    private auth: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private logger: NGXLogger,
    private modalService: ModalService
    ) { }

  ngOnInit(): void {
    if (this.auth.isLoggedIn) {
      this.logger.warn('Already logged in. Logging out...');
      this.auth.logoutObserved(false).subscribe(() => {
        this.logger.warn('Logout complete. Logging in again...');
        this.prepareTokenlogin();
      });
    } else {
      this.logger.info('Starting tokenlogin...');
      this.prepareTokenlogin();
    }
  }

  private prepareTokenlogin() {

    // first of all delete local storage settings
    this.auth.deleteLocalStorageReports();

    combineLatest([
      this.route.params,
      this.route.queryParams
    ]).subscribe(([params, queryParams]) => {
      this.tokenlogin(
        params.logintoken,
        queryParams.redirect,
        Object.keys(queryParams).indexOf('cb3') !== -1,
        Object.keys(queryParams).indexOf('cclogin') !== -1,
        Object.keys(queryParams).indexOf('parents') !== -1,
        Object.keys(queryParams).indexOf('fromhp') !== -1,
        Object.keys(queryParams).indexOf('islimitview') !== -1,
      );
    });
  }

  /**
   *
   * @param redirectUrl It does not matter whether teh URL contains a leading '/'
   */
  private tokenlogin(token: string, redirectUrl: string|undefined, isCb3: boolean, isCCLogin: boolean, isParents: boolean, fromHomepage: boolean, isLimitView: boolean) {
    const roleOverride = isParents ? UserRole.Parents : undefined;

    this.auth.isCb3 = isCb3;
    this.auth.isCCLogin = isCCLogin;

    // if login was from salfeld homepage store this information in localstorage (checked in logout process)
    if (fromHomepage === true) {
      localStorage?.setItem('fromhp', 'true');
    }

    this.auth.tokenlogin(token, roleOverride).subscribe({
      next: (result) => {
        if (result === LoginResult.Success) {
          this.logger.info('Tokenlogin successful');
          this.logger.info(`Redirecting to "${redirectUrl ?? AuthService.defaultLoginRedirectUrl}"...`);

          // Navigate and remove this route from history (don't let user go back to tokenlogin)
          // Take care of islimitview parameter and append it if necessary
          let destUrl = AuthService.defaultLoginRedirectUrl;
          if (isLimitView) {
            if (destUrl.indexOf('?') === -1) {
              destUrl = destUrl + '?islimitview=true';
            } else {
              destUrl = destUrl + '&islimitview=true';
            }
          }

          this.router.navigateByUrl(redirectUrl ?? destUrl, { replaceUrl: true });
        } else {
          this.logger.info('Tokenlogin failed:', LoginResult[result]);
          this.showErrorMessageBox();
        }
      },
      error: () => {
        this.logger.info('Tokenlogin failed: Error on API request');
        this.showErrorMessageBox();
      }
    });
  }

  private showErrorMessageBox() {
    this.modalService.showMessageBox({
      title: 'ERROR.LOGIN',
      text: 'ERROR.LOGIN_TEXT',
      icon: MessageBoxIcon.Error,
      buttons: ModalButtons.Ok
    }).onClose.subscribe(() => {
      // Navigate and remove this route from history (don't let user go back to tokenlogin)
      this.router.navigate([AuthService.defaultLogoutRedirectUrl], { replaceUrl: true });
    });
  }
}
